import { Injectable } from '@angular/core';
import { CoachNotifyParams, PendingCoachNotification } from '@cf-shared/coach-notify-interface';
import { CloudFunctionsService } from './cloud-functions.service';
import { UserDb } from './user-db.service';
import { DatabaseWrapper } from './database-wrapper';
import { LoggingService } from './logging.service';
import { errorReportFields } from './error-patterns';
import { ConnectionState, OnlineStateService } from './online-state.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { isBackgroundReadNotReady } from 'services/background-read-failure';

const PENDING_PREFIX = 'pending-coach-notify/';

@Injectable({ providedIn: 'root' })
export class CoachNotifyService {

  constructor(
    private cloudFunctions: CloudFunctionsService,
    private userDb: UserDb,
    private logging: LoggingService,
    private onlineState: OnlineStateService,
  ) {}

  async queueNotificationAsync(params: CoachNotifyParams) {
    const db = this.userDb.database;
    const docId = PENDING_PREFIX + Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
    const doc: PendingCoachNotification = {
      _id: docId,
      params,
      createdAt: new Date().toISOString(),
    };
    const transaction = DatabaseWrapper.getTransactionId();
    // The pending record is a retry safety net, not a precondition for sending. Write it if
    // we can, and go on to the send either way.
    //
    // Most callers fire this method and forget it, so a rejection escaping here would become
    // an unhandled rejection — the same reasoning `retryPendingNotificationsAsync` below
    // already applies to its own read. Since df-6ce1f9d6830e this save THROWS when the
    // database is being torn down instead of returning as though the record had been stored.
    //
    // Carrying on rather than returning is deliberate, and it keeps the behaviour this had
    // before that change: the save was skipped in silence and the send happened anyway, so
    // the coach still heard about the message. Returning here instead would drop the
    // notification outright, with nothing left to replay it — `retryPendingNotificationsAsync`
    // only ever replays a pending document, and in this window none was written. What we lose
    // by going on is only the safety net: if the send then fails there is nothing to retry
    // from. That is the smaller loss, and it is confined to a database that is going away.
    let pendingRecordStored = true;
    try {
      await db.saveRecordAsync(doc, transaction);
    } catch (err) {
      if (!isDatabaseTornDown(err)) throw err;
      pendingRecordStored = false;
      console.log('coachNotify: pending record not stored (database shutting down) — sending anyway, with no retry to fall back on');
    }

    try {
      const result = await this.cloudFunctions.coachNotifyAsync(params);
      // only a channel that was actually attempted can have "failed" — the
      // no-coach / unknown-variant no-op paths report attempted:false and must
      // not be reported as an email failure (there was no email to fail).
      if (result.attempted && !result.emailSent) {
        this.logging.trackSilentProblem('coachNotifyEmailFailed', { type: params.type, emailError: result.emailError });
      }
      if (result.sent || !result.attempted) {
        // either delivered, or nothing was attempted (e.g. no linked coach) —
        // either way there is nothing left to retry.
        //
        // Skip the delete when the save above was refused: there is no stored document to
        // remove, and the delete would throw for the same teardown reason and be reported
        // by the catch below as a deferred delivery that will be retried — which would be
        // wrong twice over, since it WAS delivered and nothing is pending.
        if (pendingRecordStored) await db.deleteRecordAsync(doc, transaction);
      } else if (pendingRecordStored) {
        // attempted and neither channel delivered. leave the pending doc in place
        // so retryPendingNotificationsAsync picks it up later — deleting it here
        // would silently drop a notification nobody ever got.
        console.log('coachNotify delivery deferred, will retry later: neither channel delivered');
      } else {
        // attempted, neither channel delivered, and there is no pending doc to replay: the
        // coach never hears about this message. Same loss as a thrown send below, so the
        // same report, with `reason` telling the two routes apart (df-97e93c3f89c7)
        console.log('coachNotify not delivered, and no retry is possible: neither channel delivered and no pending record was stored');
        this.logging.trackSilentProblem('coachNotifySendFailedNoRetry', {
          type: params.type,
          emailError: result.emailError,
          reason: 'neitherChannelDelivered',
        });
      }
    } catch (err) {
      // if the pending record was stored, retryPendingNotificationsAsync replays it and reports
      // coachNotifyFailed if it still fails while online — so this is only a deferral. If the
      // record was NOT stored, nothing will ever retry, and the coach never hears about this
      // message: that loss has to reach a debugger (#5191)
      if (pendingRecordStored) {
        console.log(`coachNotify delivery deferred, will retry later: ${err}`);
      } else {
        this.logging.trackSilentProblem('coachNotifySendFailedNoRetry', {
          type: params.type,
          ...errorReportFields(err),
        });
      }
    }
  }

  async retryPendingNotificationsAsync() {
    const db = this.userDb.database;
    let pendingDocs: PendingCoachNotification[];
    try {
      pendingDocs = await db.withDocWatcherAsync(
        watcher => db.getByIdHeadAsync(PENDING_PREFIX, watcher)) as PendingCoachNotification[];
    } catch (err) {
      // callers fire this and forget, so a rejection here would become an
      // unhandled rejection. The read THROWS when the database is unavailable
      // (deferred #2) instead of answering with an empty list, and a refused
      // remote read on resume rejects with CouchDB's own 401 object
      // (df-b99ce9dcabce). Either way there is nothing to retry against, and the
      // next trigger will try again. anything else is reported, never rethrown.
      if (isBackgroundReadNotReady(err)) {
        console.log('Retrying pending coach notifications: data not reachable right now, will retry later:', err);
        return;
      }
      this.logging.trackSilentProblem('CoachNotifyRetryReadFailed', errorReportFields(err));
      return;
    }

    if (pendingDocs.length === 0) return;

    console.log(`Retrying ${pendingDocs.length} pending coach notification(s)`);

    for (let i = 0; i < pendingDocs.length; i++) {
      const doc = pendingDocs[i];

      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }

      try {
        const result = await this.cloudFunctions.coachNotifyAsync(doc.params);
        if (result.attempted && !result.emailSent) {
          this.logging.trackSilentProblem('coachNotifyEmailFailed', { type: doc.params.type, docId: doc._id, emailError: result.emailError });
        }
        if (result.sent || !result.attempted) {
          const transaction = DatabaseWrapper.getTransactionId();
          await db.deleteRecordAsync(doc, transaction);
          console.log(result.sent
            ? `Pending coach notification sent: ${doc._id}`
            : `Pending coach notification dropped, nothing to attempt (e.g. no linked coach): ${doc._id}`);
        } else {
          console.log(`Pending coach notification still undelivered: ${doc._id}`);
          if (this.onlineState.connectionState === ConnectionState.Online) {
            this.logging.trackSilentProblem('coachNotifyFailed', { type: doc.params.type, docId: doc._id });
          }
        }
      } catch (err) {
        console.log(`Pending coach notification failed: ${doc._id}: ${err}`);
        // the delete above now THROWS when the database is being torn down instead of
        // returning as though it had removed the document (df-6ce1f9d6830e). That is our own
        // shutdown, and it says nothing about whether the notification was delivered — the
        // pending document simply survives and the next run retries it. Reporting it as a
        // delivery failure would be wrong as well as noisy.
        if (isDatabaseTornDown(err)) continue;
        if (this.onlineState.connectionState === ConnectionState.Online) {
          this.logging.trackSilentProblem('coachNotifyFailed', { type: doc.params.type, docId: doc._id });
        }
      }
    }
  }
}
